import { ChevronLeft, ChevronRight } from "@icons";
import * as DropdownMenuRadix from "@radix-ui/react-dropdown-menu";
import classNames from "classnames";
import {
  Fragment,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  MENU_DANGER_ICON_SLOT_CLASSES,
  MENU_DANGER_ITEM_CLASSES,
  MENU_ICON_SLOT_CLASSES,
  MENU_ITEM_CLASSES,
  MENU_LABEL_CLASSES,
  MENU_PANEL_CLASSES,
  MENU_SEPARATOR_CLASSES,
} from "./styles";
import {
  MenuAccountHeader,
  MenuGroup,
  MenuHeader,
  MenuItem,
  MenuTitleHeader,
} from "./types";
import {
  getInitials,
  isAccountHeader,
  isDrillable,
  resolveView,
} from "./view-stack";

const GLYPH_SIZE = 16;

const PANEL_CLASSES = MENU_PANEL_CLASSES;
const ITEM_CLASSES = MENU_ITEM_CLASSES;
const DANGER_ITEM_CLASSES = MENU_DANGER_ITEM_CLASSES;
const ICON_SLOT_CLASSES = MENU_ICON_SLOT_CLASSES;
const DANGER_ICON_SLOT_CLASSES = MENU_DANGER_ICON_SLOT_CLASSES;
const SEPARATOR_CLASSES = MENU_SEPARATOR_CLASSES;
const GROUP_LABEL_CLASSES = MENU_LABEL_CLASSES;

function sizeIcon(icon: ReactNode): ReactNode {
  if (!isValidElement(icon) || typeof icon.type === "string") {
    return icon;
  }
  const element = icon as ReactElement<{ size?: number; strokeWidth?: number }>;
  return cloneElement(element, {
    size: element.props.size ?? GLYPH_SIZE,
    strokeWidth: element.props.strokeWidth ?? 2,
  });
}

function itemTextValue(item: MenuItem): string {
  return typeof item.label === "string" ? item.label : item.key;
}

function AccountHeaderBlock({ header }: { header: MenuAccountHeader }) {
  return (
    <div className="flex items-center gap-2.5 px-2.5 pt-2 pb-2.5">
      <span
        className={classNames(
          "w-[34px] h-[34px] shrink-0 rounded-full overflow-hidden",
          "grid place-items-center",
          "bg-primary-100 text-primary-800 font-comfortaa font-semibold text-[13px]",
        )}
      >
        {header.avatar ? (
          <img
            src={header.avatar}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          getInitials(header.name)
        )}
      </span>
      <span className="min-w-0 flex flex-col gap-px">
        <span className="text-sm font-semibold text-gray-800 truncate">
          {header.name}
        </span>
        {header.email && (
          <span className="text-xs text-gray-500 truncate">{header.email}</span>
        )}
      </span>
    </div>
  );
}

function TitleHeaderBlock({ header }: { header: MenuTitleHeader }) {
  return (
    <div className="px-2.5 pt-2 pb-2.5">
      <div className="text-sm font-semibold text-gray-800">{header.title}</div>
      {header.subtitle && (
        <div className="text-xs leading-snug text-gray-500 mt-0.5">
          {header.subtitle}
        </div>
      )}
    </div>
  );
}

function HeaderBlock({ header }: { header: MenuHeader }) {
  return (
    <>
      {isAccountHeader(header) ? (
        <AccountHeaderBlock header={header} />
      ) : (
        <TitleHeaderBlock header={header} />
      )}
      <DropdownMenuRadix.Separator className={SEPARATOR_CLASSES} />
    </>
  );
}

function BackRow({ parent, onBack }: { parent: MenuItem; onBack: () => void }) {
  return (
    <>
      <DropdownMenuRadix.Item
        textValue={itemTextValue(parent)}
        onSelect={(event) => {
          event.preventDefault();
          onBack();
        }}
        className={classNames(ITEM_CLASSES, "gap-2 font-medium")}
      >
        <span
          className={classNames(
            "w-4 h-4 shrink-0 grid place-items-center",
            "text-gray-500 transition-colors duration-100",
            "group-data-[highlighted]:text-primary-500",
          )}
        >
          <ChevronLeft size={GLYPH_SIZE} />
        </span>
        <span className="flex-1 min-w-0 truncate">{parent.label}</span>
      </DropdownMenuRadix.Item>
      <DropdownMenuRadix.Separator className={SEPARATOR_CLASSES} />
    </>
  );
}

function MenuItemRow({
  item,
  onDrillIn,
}: {
  item: MenuItem;
  onDrillIn: (item: MenuItem) => void;
}) {
  const drillable = isDrillable(item);

  return (
    <DropdownMenuRadix.Item
      disabled={item.disabled}
      textValue={itemTextValue(item)}
      aria-haspopup={drillable ? "menu" : undefined}
      onSelect={(event) => {
        if (drillable) {
          event.preventDefault();
          onDrillIn(item);
          return;
        }
        item.onSelect?.();
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowRight" && drillable && !item.disabled) {
          event.preventDefault();
          event.stopPropagation();
          onDrillIn(item);
        }
      }}
      className={classNames(
        ITEM_CLASSES,
        item.danger && DANGER_ITEM_CLASSES,
        item.description && "items-start",
      )}
    >
      <span
        className={classNames(
          ICON_SLOT_CLASSES,
          item.danger && DANGER_ICON_SLOT_CLASSES,
          item.description && "mt-px",
        )}
      >
        {sizeIcon(item.icon)}
      </span>
      <span className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span className="truncate">{item.label}</span>
        {item.description && (
          <span className="text-xs leading-snug whitespace-normal text-gray-500">
            {item.description}
          </span>
        )}
      </span>
      {drillable && (
        <span
          className={classNames(
            "ml-auto shrink-0 text-gray-400 transition-colors duration-100",
            "group-data-[highlighted]:text-primary-500",
            item.danger && DANGER_ICON_SLOT_CLASSES,
          )}
        >
          <ChevronRight size={GLYPH_SIZE} />
        </span>
      )}
    </DropdownMenuRadix.Item>
  );
}

export interface MenuProps {
  /**
   * The element that opens the menu. Rendered through Radix `Trigger asChild`,
   * so it must be a single element that forwards its ref (e.g. `IconButton`,
   * a plain `button`). Radix wires `aria-haspopup`/`aria-expanded` onto it.
   */
  trigger: ReactElement;
  /**
   * Accessible name for the menu panel (`aria-label` on the `role="menu"`
   * element). Required — pass a translated string.
   */
  ariaLabel: string;
  /** The menu content: groups of items separated by hairlines. */
  groups: MenuGroup[];
  /**
   * Optional account or title block above the first group. Rendered only at
   * the root drill level.
   */
  header?: MenuHeader;
  /** Horizontal alignment of the panel relative to the trigger. */
  align?: "start" | "end";
  /** Which side of the trigger the panel opens on. */
  side?: "top" | "bottom";
  /** Fixed panel width in px; the panel otherwise grows from its 248px minimum. */
  width?: number;
  /**
   * Controlled open state. Omit to let the menu manage its own open state;
   * pass it (with `onOpenChange`) when a parent needs to close the menu
   * programmatically, e.g. after applying a filter from a `submenuContent`
   * panel.
   */
  open?: boolean;
  /** Notified when the menu opens or closes. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * The Invup pop-up menu: a floating panel of action rows opened from a
 * trigger (kebab IconButton, labelled button, avatar…). Implements the
 * Claude Design menu spec — 20px icon slot per row, group labels and
 * separators, account/title headers, destructive and disabled treatments —
 * on top of Radix DropdownMenu, following the WAI-ARIA APG menu pattern.
 *
 * Submenus drill down in place: activating a parent row swaps the panel
 * contents for the submenu, led by a back row. ArrowRight drills in,
 * ArrowLeft steps back, and Escape pops one level — it closes the menu only
 * from the root.
 */
export function Menu({
  trigger,
  ariaLabel,
  groups,
  header,
  align = "start",
  side = "bottom",
  width,
  open: controlledOpen,
  onOpenChange,
}: MenuProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const [stack, setStack] = useState<string[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  const view = resolveView(groups, stack);
  const atRoot = stack.length === 0;

  const stackKey = stack.join(" ");
  const previousStackKey = useRef(stackKey);
  useEffect(() => {
    if (previousStackKey.current === stackKey) {
      return;
    }
    previousStackKey.current = stackKey;
    const target = contentRef.current?.querySelector<HTMLElement>(
      '[role="menuitem"]:not([data-disabled]), input, button, [tabindex]',
    );
    target?.focus();
  }, [stackKey]);

  function drillIn(item: MenuItem) {
    setStack((current) => [...current, item.key]);
  }

  function drillOut() {
    setStack((current) => current.slice(0, -1));
  }

  useEffect(() => {
    if (!open) {
      setStack([]);
    }
  }, [open]);

  function handleOpenChange(nextOpen: boolean) {
    if (!isControlled) {
      setUncontrolledOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  }

  function handleContentKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ArrowLeft" && !atRoot) {
      event.preventDefault();
      event.stopPropagation();
      drillOut();
    }
  }

  return (
    <DropdownMenuRadix.Root open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuRadix.Trigger asChild>{trigger}</DropdownMenuRadix.Trigger>
      <DropdownMenuRadix.Portal>
        <DropdownMenuRadix.Content
          ref={contentRef}
          aria-label={ariaLabel}
          align={align}
          side={side}
          sideOffset={6}
          loop
          style={width ? { width } : undefined}
          className={PANEL_CLASSES}
          onKeyDown={handleContentKeyDown}
          onEscapeKeyDown={(event) => {
            if (!atRoot) {
              event.preventDefault();
              drillOut();
            }
          }}
        >
          {atRoot && header && <HeaderBlock header={header} />}
          {!atRoot && view.parent && (
            <BackRow parent={view.parent} onBack={drillOut} />
          )}
          {view.content !== undefined && view.content}
          {view.groups.map((group, index) => (
            <Fragment key={group.key}>
              {index > 0 && (
                <DropdownMenuRadix.Separator className={SEPARATOR_CLASSES} />
              )}
              {group.label && (
                <DropdownMenuRadix.Label className={GROUP_LABEL_CLASSES}>
                  {group.label}
                </DropdownMenuRadix.Label>
              )}
              <DropdownMenuRadix.Group>
                {group.items.map((item) => (
                  <MenuItemRow key={item.key} item={item} onDrillIn={drillIn} />
                ))}
              </DropdownMenuRadix.Group>
            </Fragment>
          ))}
        </DropdownMenuRadix.Content>
      </DropdownMenuRadix.Portal>
    </DropdownMenuRadix.Root>
  );
}
